import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages } from '../../types/admin';
import type { CaseStatus, PhotoStage } from '../../types/admin';
import { validateImage } from '../../services/imageService';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  /** Valgte billeder der endnu ikke er uploadet — kan ses og fjernes først. */
  const [pending, setPending] = useState<{
    file: File;
    preview: string;
  }[]>([]);
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-452" data-mp="37ezmj2vmuej7" />;
  const customer = customerById(item.customerId);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-453" data-mp="32hzhmvairhto">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-454" data-mp="32jam6xbsgtlu">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-455" data-mp="3cgyr2g33zhhq" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-456" data-mp="3jjvgr9hu9cgp">
        <div data-id="element-457" data-mp="3oi6sjprp1x1k">
          <p className="text-sm text-ink-muted" data-id="element-458" data-mp="3ohx4w8u4g1z1">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-459" data-mp="3tg8ecigi8agq">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-460" data-mp="63foaa7rgw2k">
            {customer?.name ?? item.customerName}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-461" data-mp="b17ft34hug68">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-462" data-mp="kzdivo21dsco">
          <StatusBadge status={item.status} data-id="element-463" data-mp="2hsylqq9naxqt" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-464" data-mp="2hu9xbc9c19sh">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-465" data-mp="q6lembat4vcl" />
            Slet sag
          </button>
        </div>
      </header>

      {item.quoteId && <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-466" data-mp="1iu7ryb9u7ecn">
          <p className="text-[15px] text-ink" data-id="element-467" data-mp="1du1wsl4cz9pt">
            Oprettet fra tilbud: <span className="font-medium" data-id="element-468" data-mp="18wa4hpzsxll3">{item.quoteNumber}</span>
          </p>
          <Link to={`/admin/tilbud/${item.quoteId}`} className={buttonClasses('secondary', 'sm')} data-id="element-469" data-mp="18ye5o06bg6ho">
            Åbn tilbud
          </Link>
        </section>}

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-470" data-mp="3tlham6wnp0oz">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-471" data-mp="1lchwrxpt4omp">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-472" data-mp="1q4sxh2sdgegb" />
        </div>
        <div className="mt-6" data-id="element-473" data-mp="2521vugg3eihj">
          <StatusTimeline status={item.status} data-id="element-474" data-mp="293570pn73qcf" />
        </div>
      </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-475" data-mp="29115ufgol5fu">
        <div className="space-y-5" data-id="element-476" data-mp="1v2ko7tha5uix">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-477" data-mp="1v5gpnrsc71c4">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-478" data-mp="200vyg5bhm71o">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-479" data-mp="jmma3b78wiau">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-480" data-mp="h3ix1bfyeua7">
              <div data-id="element-481" data-mp="c4oe89hzl5q3">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-482" data-mp="c57n70pnsjrz">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-483" data-mp="q4z71i2uwudw">{item.category}</dd>
              </div>
              <div data-id="element-484" data-mp="l5mc0a1u0h08">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-485" data-mp="l6fkbfgfvekh">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-486" data-mp="3tkefwfzi0xxg">{item.address || '—'}</dd>
              </div>
              <div data-id="element-487" data-mp="3pkdrq3mll8d7">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-488" data-mp="3pl5ld8wmww0u">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-489" data-mp="76d2tuc1mn5r">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-490" data-mp="2cs768xxa59q" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-491" data-mp="1era2imbuhio8">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-492" data-mp="1ep73jcspsf7d">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-493" data-mp="1jkcyf143h47l">
              <label htmlFor="ny-note" className="sr-only" data-id="element-494" data-mp="vuinwq78zrqi">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-495" data-mp="10u4zywbcb1xq" />
              <Button type="submit" variant="dark" data-id="element-496" data-mp="kviywgy7c24">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-497" data-mp="3rwf4z8if821t">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-498" data-mp="3mywsx993x6pl">
                  <div data-id="element-499" data-mp="340aq9pxxz7jk">
                    <p className="text-xs text-ink-muted" data-id="element-500" data-mp="340u713buvpw0">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-501" data-mp="178kdegggokgd">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-502" data-mp="128o4c2swq2ty">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-503" data-mp="1dilatnz6cqj" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-504" data-mp="3oh6tqmohpojm">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-505" data-mp="3ekajabf16scf">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-506" data-mp="3emewxl2qf5pw">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-507" data-mp="3jjx5vbgr1kxw">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-508" data-mp="1ctgxv7id3gwi">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-509" data-mp="1hsk3ou661r5j">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-510" data-mp="32ach8zj3zqmn">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-511" data-mp="3pzdvx4ijby0d">
                  {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-512" data-mp="2m04664vy3zsi" /> : <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-513" data-mp="2gyol4ctrpta4" />}
                  {uploading ? 'Uploader …' : 'Vælg billeder'}
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
                  const input = event.target;
                  const files = Array.from(input.files ?? []);
                  input.value = '';
                  const problems = files.map(validateImage).filter(Boolean) as string[];
                  const accepted = files.filter(file => !validateImage(file));
                  setUploadError(problems.join(' '));
                  // Billederne lægges i kø, så de kan ses og fjernes før upload.
                  setPending(current => [...current, ...accepted.map(file => ({
                    file,
                    preview: URL.createObjectURL(file)
                  }))]);
                }} data-id="element-514" data-mp="272bu5mplxtj2" />
                </label>
              </div>
            </div>

            {pending.length > 0 && <div className="mt-5 rounded-xl border border-line bg-canvas p-4" data-id="element-515" data-mp="dr7phey2o7v2">
                <p className="text-sm text-ink-soft" data-id="element-516" data-mp="hs9i3cxhhlo2">
                  {pending.length} {pending.length === 1 ? 'billede' : 'billeder'} klar til {stage}
                </p>
                <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5" data-id="element-517" data-mp="rjnn2087fdol">
                  {pending.map((entry, index) => <li key={`${entry.file.name}-${index}`} className="relative" data-id="element-518" data-mp="wjibzy8f5t70" data-mpi={index}>
                      <img src={entry.preview} alt={entry.file.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-519" data-mp="11gimclcaxi3x" />
                      <button type="button" onClick={() => {
                  URL.revokeObjectURL(entry.preview);
                  setPending(current => current.filter((_, i) => i !== index));
                }} aria-label={`Fjern ${entry.file.name}`} className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-520" data-mp="6vmp6w2pmy2m">
                        <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-521" data-mp="zih3tccuj386" />
                      </button>
                    </li>)}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-522" data-mp="31a3s365csk0y">
                  <Button size="sm" disabled={uploading} onClick={async () => {
                if (uploading) return;
                setUploading(true);
                setUploadError('');
                const result = await addPhotos(item.id, pending.map(entry => entry.file), stage);
                setUploading(false);
                if (!result.ok) {
                  // Køen bevares, så uploaden kan forsøges igen.
                  setUploadError(result.error ?? 'Billederne kunne ikke uploades.');
                  return;
                }
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
              }} data-id="element-523" data-mp="31bonsz3em90e">
                    {uploading ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-524" data-mp="2nfzvpj2wxwg5" />
                        Uploader …
                      </> : uploadError ? 'Prøv upload igen' : `Upload til ${stage}`}
                  </Button>
                  <button type="button" disabled={uploading} onClick={() => {
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
                setUploadError('');
              }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-50" data-id="element-525" data-mp="9er4h8usohb2">
                    Ryd
                  </button>
                </div>
              </div>}

            {uploadError && <p role="alert" className="mt-4 text-sm font-medium text-danger" data-id="element-526" data-mp="1sdf9a1fv2zt4">
                {uploadError}
              </p>}

            <div className="mt-5 space-y-6" data-id="element-527" data-mp="1xaoxjdril5fd">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-528" data-mp="26cepqlt808e7">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-529" data-mp="19mnv8mkxil8e">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-530" data-mp="1emjw4cybxwjq">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-531" data-mp="1ilj11xk29tb6">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-532" data-mp="1nfn9kl8z69ed" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-533" data-mp="1y85hpbarurr3">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-534" data-mp="1fbxcu8lo8iwb" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-535" data-mp="1k95tg4r68evg">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-536" data-mp="2vom2r20ha4k0">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-537" data-mp="2vnjd0xew685o">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-538" data-mp="336kab0z3n5kl">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-539" data-mp="387q3ezwo8uun">
              {customer?.name ?? item.customerName}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-540" data-mp="2tbs97n6b60l2">
              <li data-id="element-541" data-mp="2y8r6gel9qnnh">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-542" data-mp="3lxjt1kg03j5f">{customer?.email || '—'}</li>
              <li data-id="element-543" data-mp="3m0fql7nxs5tc">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-544" data-mp="3qvv581trq5qt">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-545" data-mp="3c8tkfpqggwxg">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-546" data-mp="3h6v0s2whyi58" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-547" data-mp="8rbfbq22i40q">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-548" data-mp="dijpwpf4vvfh" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-549" data-mp="24upvkpyhklev">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-550" data-mp="1vrx2ch5qtzme">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-551" data-mp="2sjqv1npccm80">Økonomi</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-552" data-mp="2oj6xij0uvglc">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-553" data-mp="2jit0rx4pft2m">
                <dt className="text-ink-soft" data-id="element-554" data-mp="2ekhq5k6l5ejc">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-555" data-mp="2ejzkxy1y3pza">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-556" data-mp="182cizx5oh0cl">
                <dt className="text-ink-soft" data-id="element-557" data-mp="134krh48y3g8x">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-558" data-mp="t1nu884lgma2">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-559" data-mp="xzz1qc7f2kp4">
                <dt className="text-ink-soft" data-id="element-560" data-mp="xy5uovsw5ygl">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-561" data-mp="12ytlphh4efat">{formatPrice(item.estimate)}</dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-562" data-mp="17rmr87kexrny" />
          </section>
        </aside>
      </div>
    </div>;
}